import { useEffect } from 'react';
import { create } from 'zustand';

import { withKey } from './key-set';

type UnsavedCopyEditsState = {
    readonly alignmentIds: ReadonlySet<string>;
    readonly setUnsaved: (alignmentId: string, unsaved: boolean) => void;
};

const useUnsavedCopyEditsStore = create<UnsavedCopyEditsState>()((set) => ({
    alignmentIds: new Set(),
    setUnsaved: (alignmentId, unsaved) =>
        set(({ alignmentIds }) => ({ alignmentIds: withKey(alignmentIds, alignmentId, unsaved) })),
}));

/** Records that the copy has edits not saved yet, until they are saved, discarded or the editor goes away. */
export const useMarkUnsavedCopyEdits = (alignmentId: string, unsaved: boolean): void => {
    const setUnsaved = useUnsavedCopyEditsStore((state) => state.setUnsaved);

    useEffect(() => {
        setUnsaved(alignmentId, unsaved);

        return () => setUnsaved(alignmentId, false);
    }, [alignmentId, setUnsaved, unsaved]);
};

/** Whether step decisions wait: answering or deciding a step would skip the copy's unsaved edits. */
export const useHasUnsavedCopyEdits = (alignmentId?: string): boolean =>
    useUnsavedCopyEditsStore(({ alignmentIds }) =>
        alignmentId === undefined ? alignmentIds.size > 0 : alignmentIds.has(alignmentId),
    );
